import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import { ICreateVariantForm } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';
import { customStyles } from '../../../utils/SelectStyles';
import FormUpload from '../../../components/formInputs/FormUpload';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import InputSkeleton from '../../../components/loaders/InputSkeleton';

const CreateVariantForm = () => {
  const { id: makeId, modelId, variantId: id } = useParams();
  const formRef = useRef(null);
  const { t, i18n } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['variants', id],
    invalidateQueryKey: ['variants'],
    singleGetApi: `/admin/variants/${id}`,
    id,
    editApi: `/admin/variants/${id}`,
    addApi: '/admin/variants',
    itemName: t('variant'),
    listRoute: id ? '' : `/vehicle-models/${makeId}/model-variants/${modelId}`
  });

  const validationSchema = Yup.object({
    name: Yup.object({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    }),
    display_name: Yup.object({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    }),
    code: Yup.string().required(t('required')),
    transmission_id: Yup.number().required(t('required')),
    body_type_id: Yup.number().required(t('required')),
    vehicle_model_id: Yup.number().required(t('required')),
    fuel_type_id: Yup.number().required(t('required')),
    // warranty_months: Yup.number().required(t('required')),
    // warranty_mileage: Yup.number().required(t('required')),
    price: Yup.number().required(t('required')),
    hidden: Yup.number().required(t('required')).oneOf([0, 1], t('required')),
    featured: Yup.number().required(t('required')).oneOf([0, 1], t('required')),
    // financing_price: Yup.number().required(t('required')),
    retail_price: Yup.number().required(t('required')),
    model_year: Yup.number().required(t('required')),
    order: Yup.number().required(t('required'))
  });

  const { modelOptions: transmissionsOptions, apiDataLoading: transmissionsLoading } = useModelSelectOptions({
    modelRoute: 'admin/transmissions',
    labelKey: `name.${i18n.language}`,
    valueKey: 'id',
    queryKey: ['transmissions']
  });
  const { modelOptions: bodyTypesOptions, apiDataLoading: bodyTypesLoading } = useModelSelectOptions({
    modelRoute: 'admin/body-types',
    labelKey: `description.${i18n.language}`,
    valueKey: 'id',
    queryKey: ['body-types']
  });
  const { modelOptions: fuelTypesOptions, apiDataLoading: fuelTypesLoading } = useModelSelectOptions({
    modelRoute: 'admin/fuel-types',
    labelKey: `name.${i18n.language}`,
    valueKey: 'id',
    queryKey: ['fuel-types']
  });

  const initialValues: ICreateVariantForm = {
    ...apiData,
    
    name: {
      ar: apiData?.name?.ar || '',
      en: apiData?.name?.en || ''
    },
    display_name: {
      ar: apiData?.display_name?.ar || '',
      en: apiData?.display_name?.en || ''
    },
    
    code: apiData?.code || '',
    transmission_id: apiData?.transmission?.id || '',
    body_type_id: apiData?.body_type?.id || '',
    fuel_type_id: apiData?.fuel_type?.id || '',
    warranty_months: apiData?.warranty_months || '',
    warranty_mileage: apiData?.warranty_mileage || '',
    price: apiData?.price || '',
    financing_price: apiData?.financing_price || '',
    retail_price: apiData?.retail_price || '',
    model_year: apiData?.model_year || '',
    
    hidden: apiData?.hidden !== undefined ? apiData.hidden : 0,
    featured: apiData?.featured !== undefined ? apiData.featured : 0,
    
    vehicle_model_id: Number(modelId),
    image: getMedia(apiData?.media, 'image')?.url || '',
    display_name_image: getMedia(apiData?.media, 'display_name_image')?.url || '',

    order: apiData?.order || '',
  };

  if (getDataLoading || !initialValues)
    return (
      <FormDynamicSkeleton
        sections={[
          [{ columns: 2, items: 2, type: 'textarea' }],
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 1 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 1, items: 1, type: 'switch' },
            { columns: 1, items: 1, type: 'switch' }
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = {
            ...values,
            vehicle_model_id: Number(modelId),
            price: Number(values.price),
            financing_price: Number(values.financing_price),
            retail_price: Number(values.retail_price),
            _method: 'POST'
          };
          delete (value as any).exterior_colors
          delete (value as any).interior_colors
          delete (value as any).vehicle_model
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => {
          return (
            <Form>
              <SectionHeader title={t('variant_images')}>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload errors={formik?.errors.image as string} touched={formik?.touched?.image as boolean} formik={formik} name={`image`} label={t('image')} />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FormUpload
                      errors={formik?.errors.display_name_image as string}
                      touched={formik?.touched?.display_name_image as boolean}
                      formik={formik}
                      name={`display_name_image`}
                      label={t('display_name_image')}
                    />
                  </FieldWrapper>
                </div>
              </SectionHeader>
              <SectionHeader title={t('variant_details')} customStyle="last">
                <div className="inputs_group">
                  <FieldWrapper title={t('name_en')} inputName={`name[en]`} inputPlaceholder={t('name_en')} input customPadding />
                  <FieldWrapper title={t('name_ar')} inputName={`name[ar]`} inputPlaceholder={t('name_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('display_name_en')} inputName={`display_name[en]`} inputPlaceholder={t('display_name_en')} input customPadding />
                  <FieldWrapper title={t('display_name_ar')} inputName={`display_name[ar]`} inputPlaceholder={t('display_name_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('code')} inputName={`code`} inputPlaceholder={t('code')} input customPadding />
                  {transmissionsLoading ? (
                    <InputSkeleton />
                  ) : (
                    <FieldWrapper
                      customPadding
                      title={t('transmission')}
                      inputPlaceholder={t('transmission')}
                      inputName={`transmission_id`}
                      options={transmissionsOptions}
                      selectStyle={customStyles}
                      select
                    />
                  )}
                </div>
                <div className="inputs_group">
                  {bodyTypesLoading ? (
                    <InputSkeleton />
                  ) : (
                    <FieldWrapper
                      customPadding
                      title={t('body_type')}
                      inputPlaceholder={t('body_type')}
                      inputName={`body_type_id`}
                      options={bodyTypesOptions}
                      selectStyle={customStyles}
                      select
                    />
                  )}
                  {fuelTypesLoading ? (
                    <InputSkeleton />
                  ) : (
                    <FieldWrapper
                      customPadding
                      title={t('fuel_type')}
                      inputPlaceholder={t('fuel_type')}
                      inputName={`fuel_type_id`}
                      options={fuelTypesOptions}
                      selectStyle={customStyles}
                      select
                    />
                  )}
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('model_year')} inputName={`model_year`} inputPlaceholder={t('model_year')} type="number" input customPadding />
                  <FieldWrapper />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('warranty_months')} inputName={`warranty_months`} inputPlaceholder={t('warranty_months')} type="number" input customPadding />
                  <FieldWrapper title={t('warranty_mileage')} inputName={`warranty_mileage`} inputPlaceholder={t('warranty_mileage')} type="number" input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('price')} inputName={`price`} inputPlaceholder={t('price')} type="number" input customPadding />
                  <FieldWrapper title={t('financing_price')} inputName={`financing_price`} inputPlaceholder={t('financing_price')} type="number" input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('retail_price')} inputName={`retail_price`} inputPlaceholder={t('retail_price')} type="number" input customPadding />
                  <FieldWrapper title={t('addon_price')} inputName={`addon_price`} inputPlaceholder={t('addon_price')} type="number" input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('order')} inputName={`order`} inputPlaceholder={t('order')} type="number" input customPadding />
                </div>
                <div className="switch_container" style={{ flex: '1 1', marginBottom: '1rem' }}>
                  <SwitchActivation onChange={(value: any) => formik.setFieldValue('hidden', value?.target?.checked ? 1 : 0)} active={formik.values.hidden} />
                  <p>
                    {t('hide_from_website')} <span className="side_note">({t('active_means_hide')})</span>
                  </p>
                </div>
                <div className="switch_container" style={{ flex: '1 1' }}>
                  <SwitchActivation onChange={(value: any) => formik.setFieldValue('featured', value?.target?.checked ? 1 : 0)} active={formik.values.featured} />
                  <p>{t('featured')}</p>
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateVariantForm;
